import { Label } from '@headlessui/react'
import clsx from 'clsx'
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

export default function CountrySelect({ onSelectCountry, className = '' }) {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(true)
    const [countries, setCountries] = useState([])
    const [selectedCountry, setSelectedCountry] = useState({
        code: 'GBR',
        name: 'United Kingdom',
        flag: 'https://flagcdn.com/w320/gb.png',
    })
    const dropdownRef = useRef(null)

    useEffect(() => {
        fetch('https://restcountries.com/v3.1/all?fields=name,flags,cca2')
            .then(res => res.json())
            .then(data => {
                const sorted = data
                    .map(c => ({
                        code: c.cca2,
                        name: c.name.common,
                        flag: c.flags?.png || '',
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name))
                setCountries(sorted)
            })
            .catch(e => console.error('Error fetching countries:', e))
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        const handleClickOutside = e => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleSelect = country => {
        if (onSelectCountry) onSelectCountry(country)
        setSelectedCountry(country)
        setIsOpen(false)
        setSearch('')
    }

    const filteredCountries = countries.filter(
        c =>
            c.name.toLowerCase().includes(search.toLowerCase()) ||
            c.code.toLowerCase().includes(search.toLowerCase())
    )

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {loading && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-white/70'>
                    <div className='w-12 h-12 border-t-4 border-yellow-500 rounded-full animate-spin' />
                </div>
            )}

            <div className='w-full space-y-1'>
                <Label className='text-sm font-medium text-gray-700'>
                    {t('cart.select_country')} <span className='text-red-500'>*</span>
                </Label>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className='w-full px-3 py-2 text-sm text-left bg-white border border-gray-300 rounded'
                >
                    {selectedCountry ? (
                        <div className='flex items-center gap-2'>
                            <img
                                src={selectedCountry.flag}
                                alt={selectedCountry.name}
                                className='w-5 h-4 rounded-sm'
                            />
                            <span>{selectedCountry.name}</span>
                        </div>
                    ) : (
                        t('cart.select_country')
                    )}
                </button>

                {isOpen && (
                    <div
                        className={clsx(
                            'w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm shadow-sm',
                            'focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none',
                            'disabled:bg-gray-100 disabled:text-gray-500',
                            className
                        )}
                    >
                        <input
                            type='text'
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className='w-full px-3 py-2 text-sm border-b focus:outline-none'
                            placeholder={t('cart.search_country')}
                        />
                        <ul className='overflow-y-auto text-sm max-h-60'>
                            {filteredCountries.map(c => (
                                <li
                                    key={c.code}
                                    onClick={() => handleSelect(c)}
                                    className='flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-gray-100'
                                >
                                    <img
                                        src={c.flag}
                                        alt={c.name}
                                        className='object-cover w-5 h-4 rounded-sm'
                                    />
                                    <span>{c.name}</span>
                                </li>
                            ))}
                            {filteredCountries.length === 0 && (
                                <li className='px-4 py-2 text-gray-500'>{t('cart.no_results')}</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    )
}
